import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Lock, Pencil, Plus, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DataTable } from "@/components/app/DataTable";
import { ImportDialog } from "@/components/app/ImportDialog";
import { useMe } from "@/components/app/MeContext";
import { useFetch } from "@/hooks/useFetch";
import { reloadLookups } from "@/hooks/useLookups";
import { api, downloadFile, errMsg } from "@/lib/api";

const toAliases = (s) => (s || "").split(";").map((x) => x.trim()).filter(Boolean);

function ValueDialog({ value, onClose, onSaved }) {
  const [f, setF] = useState({ ...value, alias_text: (value.aliases || []).join("; ") });
  const save = async () => {
    try {
      await api.put(`/lookups/${value.id}`, { value: f.value, description: f.description, aliases: toAliases(f.alias_text), sort: Number(f.sort) || null, active: f.active });
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="lookup-edit-dialog">
        <DialogHeader><DialogTitle>Edit “{value.value}”</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Value</Label><Input value={f.value} onChange={set("value")} disabled={value.system || value.used > 0} data-testid="lookup-edit-value" />
            {(value.system || value.used > 0) && <p className="text-[11px] text-slate-500 mt-1">{value.system ? "Drives app logic — cannot be renamed." : `Used on ${value.used} record(s) — rename is blocked to keep history. Add an alias instead.`}</p>}</div>
          <div className="col-span-2"><Label>Description</Label><Input value={f.description || ""} onChange={set("description")} data-testid="lookup-edit-description" /></div>
          <div className="col-span-2"><Label>Import aliases (; separated)</Label><Input value={f.alias_text} onChange={set("alias_text")} data-testid="lookup-edit-aliases" /></div>
          <div><Label>Sort order</Label><Input type="number" value={f.sort ?? ""} onChange={set("sort")} data-testid="lookup-edit-sort" /></div>
          <div><Label>Active</Label><div className="h-9 flex items-center"><Switch checked={f.active} disabled={value.system} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="lookup-edit-active" /></div></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="lookup-edit-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddValue({ type, onDone }) {
  const [nv, setNv] = useState({ value: "", description: "", alias_text: "" });
  const add = async () => {
    try {
      await api.post("/lookups", { type, value: nv.value, description: nv.description, aliases: toAliases(nv.alias_text) });
      setNv({ value: "", description: "", alias_text: "" }); toast.success("Value added"); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-col sm:flex-row gap-2 mb-3" data-testid="lookup-add-row">
      <Input className="bg-white sm:w-56" placeholder="New value" value={nv.value} onChange={(e) => setNv({ ...nv, value: e.target.value })} data-testid="lookup-new-value" />
      <Input className="bg-white sm:w-64" placeholder="Description" value={nv.description} onChange={(e) => setNv({ ...nv, description: e.target.value })} data-testid="lookup-new-description" />
      <Input className="bg-white sm:w-64" placeholder="Import aliases (; separated)" value={nv.alias_text} onChange={(e) => setNv({ ...nv, alias_text: e.target.value })} data-testid="lookup-new-aliases" />
      <Button onClick={add} disabled={!nv.value.trim()} data-testid="lookup-add-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
    </div>
  );
}

function NewList({ onDone }) {
  const [label, setLabel] = useState("");
  const add = async () => {
    try { const r = await api.post("/lookup-types", { label }); setLabel(""); onDone(r.data.key); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-2 flex gap-1 border-t border-slate-100 pt-2">
      <Input className="h-8 text-xs" placeholder="New list name" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="lookup-new-type-input" />
      <Button size="sm" variant="outline" disabled={!label.trim()} onClick={add} data-testid="lookup-add-type-btn"><Plus className="h-3.5 w-3.5" /></Button>
    </div>
  );
}

function ListPicker({ types, type, setType, admin, reload }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-2 h-fit" data-testid="lookup-type-list">
      {types.map((t) => (
        <button key={t.key} onClick={() => setType(t.key)} data-testid={`lookup-type-${t.key}`}
          className={`flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs transition-colors ${t.key === type ? "bg-slate-900 text-white" : "hover:bg-slate-100"}`}>
          <span>{t.label}{t.custom && <span className="ml-1 text-[9px] opacity-60">custom</span>}</span>
          <span className={`font-mono text-[10px] ${t.active_count ? "opacity-70" : "text-amber-500"}`}>{t.active_count}</span>
        </button>))}
      {admin && <NewList onDone={async (k) => { await reload(); setType(k); }} />}
    </div>
  );
}

export function SupportingLists({ only }) {
  const { can } = useMe();
  const admin = can("admin.settings");
  const { data: allTypes, reload: reloadTypes } = useFetch("/lookup-types");
  const types = (allTypes || []).filter((t) => (only ? t.key === only : t.key !== "document_category"));
  const [type, setType] = useState(only || "segment");
  const [edit, setEdit] = useState(null);
  const [imp, setImp] = useState(false);
  const { data: rows, reload } = useFetch(`/lookups?type=${type}&usage=true`);
  const cur = (types || []).find((t) => t.key === type);
  const refresh = () => { reload(); reloadTypes(); reloadLookups(); };
  const toggle = async (r) => {
    try { await api.put(`/lookups/${r.id}`, { value: r.value, description: r.description, aliases: r.aliases, sort: r.sort, active: !r.active }); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const cols = [
    { key: "value", label: "Value", render: (r) => <span className="font-medium">{r.value}{r.system && <Lock className="inline ml-1 h-3 w-3 text-slate-400" />}</span> },
    { key: "description", label: "Description", wrap: true }, { key: "aliases", label: "Import aliases", exp: (r) => (r.aliases || []).join("; "), render: (r) => (r.aliases || []).join("; ") },
    { key: "sort", label: "Sort", right: true }, { key: "used", label: "Used on", right: true },
    { key: "active", label: "Active", exp: (r) => (r.active ? "Yes" : "No"), render: (r) => <span onClick={(e) => e.stopPropagation()}><Switch checked={r.active} disabled={!admin || r.system} onCheckedChange={() => toggle(r)} data-testid={`lookup-toggle-${r.code}`} /></span> },
    { key: "edit", label: "", exp: false, render: (r) => admin && <Button size="sm" variant="ghost" onClick={() => setEdit(r)} data-testid={`lookup-edit-${r.code}`}><Pencil className="h-4 w-4" /></Button> },
  ];
  return (
    <div className={`grid grid-cols-1 gap-4 ${only ? "" : "lg:grid-cols-[220px_minmax(0,1fr)]"}`} data-testid="supporting-lists">
      {!only && <ListPicker types={types} type={type} setType={setType} admin={admin} reload={reloadTypes} />}
      <div>
        <div className="mb-2 min-w-0"><h3 className="text-sm font-semibold text-slate-900" data-testid="lookup-current-title">{cur?.label}</h3>
          <p className="text-xs text-slate-500">Used in: {cur?.used_in}. Forms only offer active values. Values in use cannot be renamed or deleted — mark them inactive. <Lock className="inline h-3 w-3" /> = drives app logic.</p></div>
        {admin && <AddValue type={type} onDone={refresh} />}
        <DataTable testid="lookups-table" columns={cols} rows={rows} exportName={`List_${type}`} exportPerm={only ? "logistics.export" : "admin.settings"} empty="No values yet"
          toolbar={admin && <><Button size="sm" variant="outline" onClick={() => downloadFile("/templates/lookups", "Supporting_lists_import.xlsx")} data-testid="lookup-template-btn"><Download className="h-4 w-4 mr-1" />Import sheet (all lists)</Button>
            <Button size="sm" variant="outline" onClick={() => setImp(true)} data-testid="lookup-import-btn"><FileSpreadsheet className="h-4 w-4 mr-1" />Import Excel</Button></>} />
      </div>
      <ImportDialog entity="lookups" title="Import supporting-list values" open={imp} onOpenChange={setImp} onDone={refresh} />
      {edit && <ValueDialog value={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); refresh(); }} />}
    </div>
  );
}
